import React, { useEffect } from "react";
import { NavBar, SideNav } from "../../components";
import {
  AiOutlineUser,
  AiOutlineAppstore,
  AiOutlineFileText,
  AiOutlineGlobal,
  AiOutlineBook,
  AiOutlineBank,
  AiOutlineFlag,
  AiOutlineNotification,
} from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import { timestampToTimeAgo } from "../../config/firebase";
import { fetchDashboardSummary } from "../../redux/slices/dashboard";
import { useNavigate } from "react-router-dom";

const SkeletonLoader = () => (
  <div className="animate-pulse">
    {/* Skeleton for Metrics Section */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {[...Array(8)].map((_, index) => (
        <div
          key={index}
          className="p-4 bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 space-y-4"
        >
          <div className="h-8 w-8 bg-gray-200 rounded-full dark:bg-gray-700"></div>
          <div className="h-4 bg-gray-200 rounded-full dark:bg-gray-700 w-1/2"></div>
          <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-1/3"></div>
        </div>
      ))}
    </div>

    {/* Skeleton for Recent Notifications Section */}
    <div className="p-6 bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700">
      <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-1/3 mb-4"></div>
      <ul className="space-y-4">
        {[...Array(5)].map((_, index) => (
          <li key={index} className="flex items-start space-x-4">
            <div className="h-8 w-8 bg-gray-200 rounded-full dark:bg-gray-700"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded-full dark:bg-gray-700 w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded-full dark:bg-gray-700 w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded-full dark:bg-gray-700 w-1/4"></div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const Home = () => {
  const dispatch = useDispatch();
  const dashboardStore = useSelector((state) => state.dashboard);
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(fetchDashboardSummary());
  }, [dispatch]);

  const {
    totalUsers,
    totalApplications,
    totalActiveApplications,
    totalArticles,
    totalSchools,
    totalScholarships,
    totalCountries,
    totalProgrammes,
    recentNotifications,
  } = dashboardStore.summary;

  const openNotification = (notification) => {
    if (
      notification.type === "application" &&
      notification.data.applicationId
    ) {
      navigate(`/applications/${notification.data.applicationId}`);
    } else {
      // Handle other notification types or do nothing
      console.log("Notification clicked:", notification);
    }
  };

  return (
    <>
      <div className="antialiased bg-gray-50 dark:bg-gray-900">
        <NavBar />
        <SideNav />
        <main className="p-4 md:ml-64 h-auto pt-20">
          {dashboardStore.pageLoading ? (
            <SkeletonLoader />
          ) : (
            <>
              {/* Metrics Section */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700">
                  <AiOutlineUser className="text-3xl text-purple-500 dark:text-gray-600 mb-2" />
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                    Total Users
                  </h3>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {totalUsers}
                  </p>
                </div>
                <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700">
                  <AiOutlineAppstore className="text-3xl text-purple-500 dark:text-gray-600 mb-2" />
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                    Total Applications
                  </h3>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {totalApplications}
                  </p>
                </div>
                <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700">
                  <AiOutlineAppstore className="text-3xl text-purple-500 dark:text-gray-600 mb-2" />
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                    Active Applications
                  </h3>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {totalActiveApplications}
                  </p>
                </div>
                <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700">
                  <AiOutlineFileText className="text-3xl text-purple-500 dark:text-gray-600 mb-2" />
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                    Total Articles
                  </h3>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {totalArticles}
                  </p>
                </div>
                <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700">
                  <AiOutlineBank className="text-3xl text-purple-500 dark:text-gray-600 mb-2" />
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                    Total Schools
                  </h3>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {totalSchools}
                  </p>
                </div>
                <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700">
                  <AiOutlineBook className="text-3xl text-purple-500 dark:text-gray-600 mb-2" />
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                    Total Scholarships
                  </h3>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {totalScholarships}
                  </p>
                </div>
                <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700">
                  <AiOutlineFlag className="text-3xl text-purple-500 dark:text-gray-600 mb-2" />
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                    Total Countries
                  </h3>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {totalCountries}
                  </p>
                </div>
                <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700">
                  <AiOutlineGlobal className="text-3xl text-purple-500 dark:text-gray-600 mb-2" />
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                    Total Programmes
                  </h3>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {totalProgrammes}
                  </p>
                </div>
              </div>

              {/* Recent Notifications Section */}
              <div className="p-6 bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">
                  Recent Notifications
                </h2>
                {(recentNotifications || []).length > 0 ? (
                  <ul className="space-y-4">
                    {recentNotifications.map((notification) => (
                      <li
                        key={notification.id}
                        className={`flex items-start space-x-4 ${
                          notification.type === "application" &&
                          notification.data.applicationId
                            ? "cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded-lg"
                            : ""
                        }`}
                        onClick={() => openNotification(notification)}
                      >
                        <AiOutlineNotification className="text-xl text-purple-500 dark:text-gray-600 mt-1" />
                        <div>
                          <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                            {notification.title}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {notification.description}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-500">
                            {timestampToTimeAgo(notification.createdAt)}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 italic">
                    No recent notifications available.
                  </p>
                )}
              </div>
            </>
          )}
        </main>
      </div>
    </>
  );
};

export default Home;
